import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Badge, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getBreeds, getAkcGroups } from '../services/breedService';
import { Breed } from '../types/breed';

const BreedsPage: React.FC = () => {
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [filteredBreeds, setFilteredBreeds] = useState<Breed[]>([]);
  const [akcGroups, setAkcGroups] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [foundOnly, setFoundOnly] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch breeds and AKC groups
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all breeds
        const breedsData = await getBreeds();
        setBreeds(breedsData);
        setFilteredBreeds(breedsData);

        // Get unique AKC groups from breeds
        const uniqueGroups = Array.from(new Set(breedsData.map(breed => breed.akc_group)));
        setAkcGroups(uniqueGroups);
      } catch (err) {
        setError('Failed to load breeds. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter breeds when search query or selected group changes
  useEffect(() => {
    let result = [...breeds];

    // Filter by AKC group if selected
    if (selectedGroup) {
      result = result.filter(breed => breed.akc_group === selectedGroup);
    }

    // Filter by found on platform
    if (foundOnly) {
      result = result.filter(breed => (breed.pup_count || 0) > 0);
    }

    // Filter by search query if entered
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(breed =>
        breed.name.toLowerCase().includes(query)
      );
    }

    setFilteredBreeds(result);
  }, [breeds, selectedGroup, searchQuery, foundOnly]);

  // Handle AKC group selection
  const handleGroupSelect = (group: string) => {
    setSelectedGroup(group === selectedGroup ? '' : group);
  };

  // Handle search input
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedGroup('');
    setSearchQuery('');
    setFoundOnly(false);
  };

  return (
    <div>
      <h1 className="text-center mb-4">Meet the Breeds</h1>

      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <div className="mb-3">
                <Form.Label>Search Breeds</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search by breed name..."
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                  {(searchQuery || selectedGroup || foundOnly) && (
                    <Button
                      variant="outline-secondary"
                      onClick={clearFilters}
                      className="ms-2"
                    >
                      Clear
                    </Button>
                  )}
                </InputGroup>
              </div>

              <div className="mb-3">
                <Form.Label>Filter by AKC Group</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {akcGroups.map(group => (
                    <Badge
                      key={group}
                      bg={selectedGroup === group ? "info" : "secondary"}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleGroupSelect(group)}
                      className="p-2"
                    >
                      {group} ({breeds.filter(b => b.akc_group === group).length})
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <Form.Check
                  type="switch"
                  id="found-toggle"
                  label="Found on PupNet"
                  checked={foundOnly}
                  onChange={() => setFoundOnly(!foundOnly)}
                />
              </div>

              <div className="mt-3">
                <p>
                  <small className="text-muted">
                    Showing {filteredBreeds.length} of {breeds.length} breeds
                  </small>
                </p>
              </div>
            </Col>

            <Col md={8}>
              {loading ? (
                <div className="text-center p-5">Loading breeds...</div>
              ) : error ? (
                <div className="alert alert-danger">{error}</div>
              ) : (
                <Row>
                  {filteredBreeds.length === 0 ? (
                    <Col>
                      <div className="text-center p-4">
                        <p>No breeds found matching your filters.</p>
                        <Button variant="outline-primary" onClick={clearFilters}>
                          Clear Filters
                        </Button>
                      </div>
                    </Col>
                  ) : (
                    filteredBreeds.map(breed => (
                      <Col key={breed.id} md={6} className="mb-3">
                        <Card className="h-100 breed-card">
                          <Link to={`/breeds/${breed.id}`} className="card-link" aria-label={`View details for ${breed.name}`}></Link>
                          <Card.Body>
                            <Card.Title>{breed.name}</Card.Title>
                            <div className="d-flex align-items-center gap-1 mb-2">
                              <Badge bg="" className="akc-group-badge">
                                {breed.akc_group}
                              </Badge>
                              {breed.is_rare && (
                                <Badge className="rare-badge">Rare</Badge>
                              )}
                            </div>
                            {breed.description && (
                              <Card.Text className="mt-2 breed-description-clamp">
                                {(() => {
                                  const idx = breed.description.indexOf(' is ');
                                  if (idx === -1) return breed.description;
                                  const after = breed.description.slice(idx + 4);
                                  return after.charAt(0).toUpperCase() + after.slice(1);
                                })()}
                              </Card.Text>
                            )}
                          </Card.Body>
                          <Card.Footer>
                            <small className="text-muted">
                              {breed.pup_count
                                ? `${breed.pup_count} ${breed.pup_count === 1 ? 'pup' : 'pups'}`
                                : '\u00A0'}
                            </small>
                          </Card.Footer>
                        </Card>
                      </Col>
                    ))
                  )}
                </Row>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default BreedsPage;
